import { LexoRank } from 'lexorank';
import dayjs, { Dayjs } from 'dayjs';

import { OriginalMenuCategory, OriginalMenuCategoryData } from 'models/entities/original-menu-category';
import { DishCategoryCollection, DishCategoryCollectionData } from 'models/entities/dish-category';

import { CreateGql, UpdateGql, DeleteGql } from './gql';

type Data = {
  id?: string;
  businessId: string;
  original: OriginalMenuCategoryData | OriginalMenuCategory;
  order: string | LexoRank;
  createdAt?: string;
  updatedAt?: string;
  dishCategories?: DishCategoryCollectionData;
};

type DataToBuild = {
  businessId: string;
  original: OriginalMenuCategory;
};

type DataToEdit = {
  dishCategories?: DishCategoryCollection;
};

type DataToCreate = {
  originalId: string;
  order: string;
};

type DataToUpdate = {
  order: string;
};

type Prev = MenuCategory | undefined;
type Next = MenuCategory | undefined;

class MenuCategory {

  readonly id?: string;
  readonly businessId: string;
  readonly original: OriginalMenuCategory;
  readonly order: LexoRank;
  readonly createdAt?: Dayjs;
  readonly updatedAt?: Dayjs;
  readonly dishCategories: DishCategoryCollection;

  private readonly createGql: CreateGql;
  private readonly updateGql: UpdateGql;
  private readonly deleteGql: DeleteGql;

  constructor(data: Data) {
    this.id = data.id;
    this.businessId = data.businessId;
    this.original = data.original instanceof OriginalMenuCategory ? data.original : new OriginalMenuCategory(data.original);
    this.order = data.order instanceof LexoRank ? data.order : LexoRank.parse(data.order);
    this.createdAt = data.createdAt ? dayjs(data.createdAt) : undefined;
    this.updatedAt = data.updatedAt ? dayjs(data.updatedAt) : undefined;
    this.dishCategories = new DishCategoryCollection(this.id, data.dishCategories);
    this.createGql = new CreateGql();
    this.updateGql = new UpdateGql();
    this.deleteGql = new DeleteGql();
  }

  edit(data: DataToEdit): this {
    return Object.assign(Object.create(this.constructor.prototype), { ...this, ...data });
  }

  moveBetween(prev: Prev, next: Next): this {
    const order = MenuCategory.getOrderBetween(prev, next);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, order });
  }

  async save(): Promise<MenuCategory> {
    return this.id ? this.update() : this.create();
  }

  private async create(): Promise<MenuCategory> {
    const createGql = await this.createGql.fetch({ businessId: this.businessId, input: this.getDataToCreate() });
    if (!createGql.document) throw new Error('invalid document');
    return new MenuCategory(createGql.document);
  }

  private async update(): Promise<MenuCategory> {
    if (!this.id) throw new Error('invalid relationship');
    const updateGql = await this.updateGql.fetch({ id: this.id, input: this.getDataToUpdate() });
    if (!updateGql.document) throw new Error('invalid document');
    return new MenuCategory(updateGql.document);
  }

  private getDataToCreate(): DataToCreate {
    const { original, order } = this;
    return { originalId: original.id!, order: order.toString() };
  }

  private getDataToUpdate(): DataToUpdate {
    const { order } = this;
    return { order: order.toString() };
  }

  async delete(): Promise<MenuCategory> {
    if (!this.id) throw new Error('invalid relationship');
    const deleteGql = await this.deleteGql.fetch({ id: this.id });
    if (!deleteGql.document) throw new Error('invalid document');
    return new MenuCategory(deleteGql.document);
  }

  toJSON(): Data {
    const { id, businessId } = this;
    const original = this.original.toJSON();
    const order = this.order.toString();
    const createdAt = this.createdAt?.toJSON();
    const updatedAt = this.updatedAt?.toJSON();
    const dishCategories = this.dishCategories.toJSON();
    return { id, businessId, original, order, createdAt, updatedAt, dishCategories };
  }

  static sort(categories: MenuCategory[]) {
    function orderByOrder(a: MenuCategory, b: MenuCategory) {
      if (a.order.toString() < b.order.toString()) return -1;
      else if (a.order.toString() > b.order.toString()) return 1;
      else return 0;
    }
    return categories.sort(orderByOrder);
  }

  static buildNext(list: MenuCategory[], data: DataToBuild): MenuCategory {
    const order = list[list.length - 1]?.order.genNext() ?? LexoRank.middle();
    return new MenuCategory({ ...data, order });
  }

  static buildBetween(prev: Prev, next: Next, data: DataToBuild): MenuCategory {
    const order = MenuCategory.getOrderBetween(prev, next);
    return new MenuCategory({ ...data, order });
  }

  private static getOrderBetween(prev: Prev, next: Next): LexoRank {
    switch (true) {
      case !prev && !next: return LexoRank.middle();
      case !prev && !!next: return next!.order.genPrev();
      case !!prev && !next: return prev!.order.genNext();
      default: return prev!.order.between(next!.order);
    }
  }

}

export { MenuCategory };
export type {
  Data as MenuCategoryData,
  DataToCreate as MenuCategoryDataToCreate,
  DataToUpdate as MenuCategoryDataToUpdate,
};

export * from './collection';